import { useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import {
	HardArrowLeft,
	LogoSmall,
	PlaneEmptyYellow
} from '@/resources/assets/images';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@/hooks/useQuery';
import { dashboardSetSelectedList } from '@/modules/dashboard/dashboardActions';
import { useListDetails } from '@/modules/lists/details/src/hooks/useListDetails';
import { cn } from '@/lib/classNames';
import Button from '@/components/Button/newButtons';
import StepsContainer from '../components/sequencesPage/StepsContainer';
import StepsContainerNav from '../components/sequencesPage/StepsContainerNav';
import SequencesContainerHeader from '../components/sequencesPage/SequencesContainerHeader';
import SequencesList from '../components/sequencesPage/SequencesList';
import {
	SequencePagesType,
	sequenceStates,
	useSequencesContext
} from '../SequencesContext';
import { SEQUENCES_PER_PAGE } from '../config';
import { useSequences } from '../hooks/useSequences';
import useStartSequence from '../hooks/useStartSequence';
import HeaderRefactored from '../../../components/HeaderRefactored/HeaderRefactored';
import { useIsScreenWidthSmallerThan } from '../../../hooks/useIsScreenWidthSmallerThan';

export default function SequencesPage() {
	const history = useHistory();
	const searchQuery = useQuery();
	const dispatch = useDispatch();
	const { getPaginatedSequences } = useSequences();
	const paginatedSequences = getPaginatedSequences();

	const {
		setSequenceEnroll,
		selectedSequence,
		setSequencePage,
		sequences,
		setSelectedTemplate,
		setSelectedSequence,
		setSelectedStep,
		templates,
		setShowAiSequenceModal
	} = useSequencesContext();

	const id = searchQuery.get('id');

	const sequenceId = useMemo(
		() => searchQuery.get('sequenceId'),
		[searchQuery]
	);

	const listIdDetail = useListDetails({
		id: id || ''
	});

	useStartSequence();
	// Makes sure we have access to the user object to determine access
	const [user] = useSelector<RootState, [any]>(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	// Defines the terms of access. Currently, to avoid interruption,
	// it has a feature flag as well as allows access to all admins/VAs
	useEffect(() => {
		if (user && user.email) {
			if (
				user?.threadedEmail === '' &&
				!user.re2SharedDomain &&
				!user.re2HostedEmail
			) {
				window.location.pathname = '/';
			}
		}
	}, [user, user.email]);

	useEffect(() => {
		const state = searchQuery.get('state');
		const showAiSequenceModal = searchQuery.get('showAiSequenceModal');

		if (state && sequenceStates.includes(state as SequencePagesType)) {
			if (showAiSequenceModal) {
				setShowAiSequenceModal(true);
			}

			if (state === 'ADD-BUSINESS') {
				if (!sequenceId || !sequences) return;

				const sequence = sequences.find(seq => seq.id === sequenceId);
				if (!sequence) return;

				setSequenceEnroll(true);

				setSelectedSequence(sequence);

				setSelectedTemplate({
					name: sequence.name,
					steps: sequence.steps
				});

				setSelectedStep(sequence.steps[0]);
			}

			history.replace('/sequences');
			setSequencePage(state as SequencePagesType);

			if (listIdDetail && listIdDetail.list) {
				dispatch(dashboardSetSelectedList(listIdDetail.list));
			}
		}
	}, [
		dispatch,
		history,
		listIdDetail,
		searchQuery,
		selectedSequence,
		sequenceId,
		sequences,
		setSelectedSequence,
		setSelectedStep,
		setSelectedTemplate,
		setSequenceEnroll,
		setSequencePage,
		setShowAiSequenceModal,
		templates
	]);

	const hasSelectedSequence = selectedSequence?.id !== undefined;
	const screenSize = useIsScreenWidthSmallerThan(1024);

	return (
		<div className="flex h-full flex-col gap-2 text-dm-white xl:pr-2">
			<HeaderRefactored
				icon={<PlaneEmptyYellow />}
				name="My Sequences"
				description="Automate outreach with a series of scheduled actions."
			/>
			<div className="mb-2 flex h-full overflow-hidden">
				<div className="flex h-full w-full flex-shrink-0 flex-col lg:w-[35.125rem]">
					<SequencesContainerHeader />
					<SequencesList paginatedSequences={paginatedSequences} />
				</div>
				<div
					className={cn(
						'absolute right-0 top-0 z-50 flex h-full w-full flex-col overflow-x-hidden rounded border-b border-l border-t border-dm-grey bg-dm-black lg:static',
						screenSize.isSmaller && !hasSelectedSequence && 'hidden'
					)}
				>
					<StepsContainerNav />
					<Button
						onClick={() => setSelectedSequence(undefined)}
						variant="text"
						leftIcon={<HardArrowLeft />}
						className="my-4 lg:hidden"
					>
						Return
					</Button>
					{hasSelectedSequence ? (
						<StepsContainer />
					) : (
						<NoSequenceSelectedTooltip />
					)}
				</div>
			</div>
			<div className="h-[51px] flex-shrink-0 rounded-tl-lg bg-dm-black px-4 py-2">
				<Pagination />
			</div>
		</div>
	);
}

function NoSequenceSelectedTooltip() {
	return (
		<div className="flex h-full flex-col gap-2 bg-dm-black p-6">
			<div className="flex items-center gap-3 rounded-2xl border border-[#757575] bg-[#303030] px-10 py-6">
				<div className="flex h-8 w-8 items-center justify-center">
					<LogoSmall />
				</div>
				<span className="text-lg font-semibold text-white">
					Create a new sequence, and preview each step of your
					sequence here
				</span>
			</div>
			<div className="h-full rounded-2xl border border-[#757575] bg-[#303030] p-6">
				<div className="flex flex-col gap-3 rounded-2xl bg-dm-black3.5 px-4 py-8 text-base">
					<span className="text-dm-white">
						Start a sequence quickly with one of our expertly
						crafted pre-made templates and adapt as needed.
					</span>
					<span className="text-dm-white">
						Add personalized messages, set follow-up timings, and
						ensure your sequence is tailored to your audience.
					</span>
				</div>
			</div>
		</div>
	);
}

function Pagination() {
	const { currentPage, setCurrentPage, sequences } = useSequencesContext();

	const totalPages = Math.ceil((sequences?.length || 0) / SEQUENCES_PER_PAGE);

	const pageNumbers = getPaginationRange(currentPage, totalPages);

	const nextPage = () => {
		setCurrentPage(currentPage + 1);
	};

	const previousPage = () => {
		setCurrentPage(currentPage - 1);
	};

	useEffect(() => {
		// Paginate back when deleting the last sequence of a page
		if (currentPage > 1 && currentPage > totalPages) {
			previousPage();
		}
	}, [sequences]);

	if (pageNumbers.length === 0) return null;

	return (
		<div className="flex items-center gap-2">
			<button
				type="button"
				onClick={previousPage}
				disabled={currentPage === 1}
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
					strokeWidth={1.5}
					stroke="currentColor"
					className="h-[18px] w-[18px] text-dm-gold disabled:text-dark-grey"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="M15.75 19.5 8.25 12l7.5-7.5"
					/>
				</svg>
			</button>
			{pageNumbers.map((page, index) => {
				if (page === -1) {
					return <div key={index}>...</div>;
				}
				return (
					<PaginationButton
						key={index}
						pageNumber={page}
						isSelected={currentPage === page}
						setCurrentPage={setCurrentPage}
					/>
				);
			})}

			<button
				type="button"
				onClick={nextPage}
				disabled={currentPage === totalPages}
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
					strokeWidth={1.5}
					stroke="currentColor"
					className="h-[18px] w-[18px] text-dm-gold disabled:text-dark-grey"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						d="m8.25 4.5 7.5 7.5-7.5 7.5"
					/>
				</svg>
			</button>
		</div>
	);
}

function PaginationButton({
	pageNumber,
	isSelected,
	setCurrentPage
}: {
	pageNumber: number;
	isSelected: boolean;
	setCurrentPage: (page: number) => void;
}) {
	function handleClick() {
		if (isSelected) return;
		setCurrentPage(pageNumber);
	}

	return (
		<button
			type="button"
			onClick={handleClick}
			className={`flex h-[35px] w-[35px] items-center justify-center rounded-full font-bold hover:border hover:border-orange hover:text-orange ${isSelected && 'border border-orange text-orange'} `}
		>
			{pageNumber}
		</button>
	);
}

function getPaginationRange(currentPage: number, maxPages: number) {
	const range = [];
	const delta = 2; // Number of pages before and after the current page

	// Define the window range
	const left = currentPage - delta;
	const right = currentPage + delta;

	for (let i = 1; i <= maxPages; i++) {
		if (i === 1 || i === maxPages) {
			range.push(i);
			continue;
		}
		if (i === left || i === right) {
			range.push(-1);
			continue;
		}
		if (i < left || i > right) {
			continue;
		}
		if (i > right) {
			range.push(i);
			continue;
		}
		range.push(i);
	}

	return range;
}
